import { useEffect, useMemo, useState } from "react";
import "./TalentRequestsPage.css";
import { Link, useHistory, useParams } from "react-router-dom/cjs/react-router-dom.min";
import { getCandidateList, getJobList } from "./Service/RecruitmentService";

/* ---------------- helpers ---------------- */
// missing / empty -> "-"
const v = (x) =>
  x === undefined || x === null || String(x).trim() === "" ? "-" : x;

const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return isNaN(d) ? null : d;
};

const fmtShort = (s) => {
  const d = parseDate(s);
  return d ? d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "-";
};

const fmt12 = (d) =>
  d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });

const timeRange = (start, mins) => {
  if (!start) return "-";
  const [h, m] = start.split(":");
  const s = new Date();
  s.setHours(+h, +m, 0, 0);
  const e = new Date(s.getTime() + (mins || 0) * 60000);
  return `${fmt12(s)} – ${fmt12(e)}`;
};

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "-";

const stageClass = (stage = "") =>
  stage.startsWith("Offer")
    ? "stage-offer"
    : `stage-${stage.toLowerCase().replace(/\s+/g, "-")}`;

const roundDot = (s) => (s === "Cleared" ? "rd-cleared" : s === "Not Cleared" ? "rd-failed" : "rd-pending");
const roundStatusCls = (s) => (s === "Cleared" ? "ir-cleared" : s === "Not Cleared" ? "ir-failed" : "ir-pending");
const dividerCls = (s) => (s === "Cleared" ? "green" : s === "Not Cleared" ? "red" : "amber");

const STAGE_FILTERS = ["All", "Applied", "Screening", "Interview", "Shortlisted", "Offer", "Rejected"];

function JobDetailRecruitment() {
  const [activeTab, setActiveTab] = useState("pipeline");
  const [addCandTab, setAddCandTab] = useState("existing");

  const [modals, setModals] = useState({
    move: false, schedule: false, feedback: false, reject: false, addCand: false,
    hire: false, offer: false, hold: false, close: false, reschedule: false,
  });
  const [selectedCandidate, setSelectedCandidate] = useState("");

  /* ---------- data state ---------- */
  const [jobList, setJobList] = useState([]);
  const [candidateList, setCandidateList] = useState([]);
  const [loading, setLoading] = useState(false);

  // values sent to the candidate API
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");      // debounced -> API `Search`
  const [hasResume] = useState(0);               // -> API `HasReseme`   (0 | 1)
  const [addedFrom] = useState("");              // -> API `AddedFrom`   (YYYY-MM-DD)
  const [addedTo] = useState("");                // -> API `AddedTo`     (YYYY-MM-DD)
  const [stageFilter, setStageFilter] = useState("All");
  const history = useHistory();
  const { Guid } = useParams();
  const job = jobList?.[0] || null;

  /* ---------- modal helpers (unchanged) ---------- */
  const openModal = (name, candidateName = "") => {
    if (candidateName) setSelectedCandidate(candidateName);
    setModals((prev) => ({ ...prev, [name]: true }));
  };
  const closeModal = (name) => setModals((prev) => ({ ...prev, [name]: false }));

  const toast = (msg, type = "success") => {
    const t = document.createElement("div");
    t.className = "og-toast";
    const color = type === "warn" ? "#fbbf24" : type === "error" ? "#f87171" : "#4ade80";
    const icon =
      type === "warn" ? "fa-triangle-exclamation" : type === "error" ? "fa-times-circle" : "fa-check-circle";
    t.innerHTML = `<i class="fa-solid ${icon}" style="color:${color};"></i>${msg}`;
    document.body.appendChild(t);
    setTimeout(() => {
      t.style.opacity = "0";
      setTimeout(() => t.remove(), 300);
    }, 2800);
  };

  const showAddTab = (tab) => setAddCandTab(tab);
  const confirmMove = () => { closeModal("move"); toast(`${selectedCandidate || "Candidate"} moved to new stage`); };
  const confirmSchedule = () => { closeModal("schedule"); toast("Interview scheduled — invite sent"); };
  const submitFeedback = () => { closeModal("feedback"); toast("Feedback submitted"); };
  const confirmReject = () => { closeModal("reject"); toast(`${selectedCandidate || "Candidate"} rejected`, "warn"); };
  const confirmAddCand = () => { closeModal("addCand"); toast("Candidate added to pipeline"); };
  const confirmHire = () => { closeModal("hire"); toast(`${selectedCandidate || "Candidate"} marked as joined`); };
  const confirmOffer = () => { closeModal("offer"); toast("Offer sent to candidate"); };

  /* ---------- debounce search ---------- */
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ---------- job API ---------- */
  useEffect(() => {
    const fetchJobList = async () => {
      setLoading(true);
      try {
        const params = {
          JobId: 0,
          Guid: Guid,
          TalentRequestId: 0,
          RecruiterEmpId: 0,
          JobStatusId: "",
          VisibilityId: "",
          EmploymentTypeId: "",
          WorkModeId: "",
          DepartmentId: "",
          Search: "",
          CreatedFrom: "",
          CreatedTo: "",
          PageNumber: 1,
          PageSize: 1,
          SortBy: "",
          SortOrder: "",
          ExperienceRangeId: "",
          SalaryRangeId: "",
          IsPublished: 0,
          Location: "",
          ExperienceMin: 0,
          ExperienceMax: 0,
          PostedAfter: "",
          CreatedByEmpId: 0,
        };
        const response = await getJobList(params);
        setJobList(response?.Data?.Jobs || []);
      } catch (e) {
        toast("Failed to load job", "error");
        setJobList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchJobList();
    // eslint-disable-next-line
  }, [Guid]);

  /* ---------- candidate API (refetch on job / search / filters) ---------- */
  useEffect(() => {
    if (!job?.Id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = {
          CandidateId: 0,
          Guid: "",
          JobId: job.Id,
          CandidatureStageId: "",
          CandidateSourceId: "",
          WorkModePreferenceId: "",
          HasReseme: hasResume,
          Search: search,
          AddedFrom: addedFrom,
          AddedTo: addedTo,
          PageNumber: 1,
          PageSize: 100,
          SortBy: "",
          SortOrder: "",
          ExperienceRangeId: "",
          SalaryRangeId: "",
        };
        const response = await getCandidateList(params);
        if (!cancelled) setCandidateList(response?.Data?.Candidates || []);
      } catch (e) {
        if (!cancelled) {
          toast("Failed to load candidates", "error");
          setCandidateList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [job?.Id, search, hasResume, addedFrom, addedTo]);

  /* ---------- derived data ---------- */
  const getCandidature = (c) =>
    (c.Candidatures || []).find((x) => x.JobId === job?.Id) || c.Candidatures?.[0] || {};

  const getRounds = (c) =>
    (c.InterviewRounds || [])
      .filter((r) => r.JobId === job?.Id)
      .sort((a, b) => new Date(a.ScheduledDate) - new Date(b.ScheduledDate));

  const matchesFilter = (stage = "", f) =>
    f === "All" ? true : f === "Offer" ? stage.startsWith("Offer") : stage === f;

  const countFor = (f) =>
    candidateList.filter((c) => matchesFilter(getCandidature(c).CandidatureStage, f)).length;

  const visibleCandidates = useMemo(
    () => candidateList.filter((c) => matchesFilter(getCandidature(c).CandidatureStage, stageFilter)),
    // eslint-disable-next-line
    [candidateList, stageFilter, job]
  );

  const interviews = useMemo(
    () =>
      candidateList
        .flatMap((c) => getRounds(c).map((r) => ({ ...r, candidateName: c.FullName })))
        .sort((a, b) => new Date(b.ScheduledDate) - new Date(a.ScheduledDate)),
    // eslint-disable-next-line
    [candidateList, job]
  );

  const stageCount = (name) =>
    job ? (job.StageCounts || []).find((s) => s.Stage === name)?.CandidateCount ?? 0 : "-";

  const mustSkills = (job?.Skills || []).filter((s) => s.RequirementType === "Must");
  const prefSkills = (job?.Skills || []).filter((s) => s.RequirementType === "Preferred");
  const mustReqs = (job?.Requirements || []).filter((r) => r.RequirementType === "Must");
  const prefReqs = (job?.Requirements || []).filter((r) => r.RequirementType === "Preferred");

  const goProfile = () => (navigation) => {
    history.push(`/Recruitment/Talent-Candidate-Profile/${Guid}`);
  };

  const jobLabel = `${v(job?.JobCode)} — ${v(job?.Title)}`;



  return (
    <>
      <div className="topbar">
        <i className="fa-solid fa-clipboard-list"></i>
        <div className="breadcrumb topbar-breadcrumb">
          <a>Recruitment</a>
          <i className="fa-solid fa-chevron-right"></i>
          <strong className="topbar-current">Talent Requests</strong>
        </div>
        <div className="top-right">
          <div className="top-user">
            <div className="top-avatar">AM</div>
            <div>
              <div className="top-user-name"> Arun Mishra </div>
              <div className="top-user-role"> HR Admin </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid position-relation">
        <div className="job-header">
          <div className="jh-top">
            <div className="jh-left">
              <div className="jh-code">
                <span>{v(job?.JobCode)}</span>
                <span className="jh-open-days">
                  <i className="fa-solid fa-clock"></i> Open {v(job?.JobAgeInDays)} days
                </span>
                <span className="jh-status-open"> {v(job?.JobStatus)} </span>
              </div>
              <div className="jh-title"> {v(job?.Title)} </div>
              <div className="jh-chips">
                <span className="jh-chip"><i className="fa-solid fa-building"></i> {v(job?.DepartmentName)}</span>
                <span className="jh-chip"><i className="fa-solid fa-location-dot"></i> {v(job?.Location)}</span>
                <span className="jh-chip"><i className="fa-regular fa-clock"></i> {v(job?.EmploymentType)}</span>
                <span className="jh-chip"><i className="fa-solid fa-house-laptop"></i> {v(job?.WorkMode)}</span>
                <span className="jh-chip"><i className="fa-solid fa-briefcase"></i> {v(job?.ExperienceRange)}</span>
                <span className="jh-chip"><i className="fa-solid fa-indian-rupee-sign"></i> {v(job?.SalaryRange)}</span>
                <span className="jh-chip"><i className="fa-solid fa-user"></i> {v(job?.RecruiterName)}</span>
              </div>
            </div>
            <div className="jh-actions">
              <Link to="/Recruitment/Reimbursement-Job/Create" className="btn btn-outline btn-sm">
                <i className="fa-solid fa-pen"></i> Edit
              </Link>
              <button className="btn btn-warning btn-sm" onClick={() => openModal("hold")}>
                <i className="fa-solid fa-pause"></i> Hold
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => openModal("close")}>
                <i className="fa-solid fa-xmark"></i> Close Job
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => toast("Share link copied")}>
                <i className="fa-solid fa-link"></i> Share
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => openModal("addCand")}>
                <i className="fa-solid fa-user-plus"></i> Add Candidate
              </button>
            </div>
          </div>
          <div className="jh-stats">
            <div className="jh-stat">
              <div className="jh-stat-val jh-stat-primary">{v(job?.TotalCandidates)}</div>
              <div className="jh-stat-lbl">Total</div>
            </div>
            <div className="jh-stat">
              <div className="jh-stat-val jh-stat-applied">{stageCount("Applied")}</div>
              <div className="jh-stat-lbl">Applied</div>
            </div>
            <div className="jh-stat">
              <div className="jh-stat-val jh-stat-interview">{v(job?.CandidatesInInterview)}</div>
              <div className="jh-stat-lbl">In Interview</div>
            </div>
            <div className="jh-stat">
              <div className="jh-stat-val jh-stat-shortlisted">{stageCount("Shortlisted")}</div>
              <div className="jh-stat-lbl">Shortlisted</div>
            </div>
            <div className="jh-stat">
              <div className="jh-stat-val jh-stat-offer">{v(job?.CandidatesOfferedOrHired)}</div>
              <div className="jh-stat-lbl">Offer / Hired</div>
            </div>
          </div>
        </div>

        <div className="tab-bar job-tabbar">
          <button className={`tab-btn ${activeTab === "pipeline" ? "active" : ""}`} onClick={() => setActiveTab("pipeline")}>
            <i className="fa-solid fa-pipe-section"></i> Pipeline
            <span className="tab-count">{job ? candidateList.length : "-"}</span>
          </button>
          <button className={`tab-btn ${activeTab === "interviews" ? "active" : ""}`} onClick={() => setActiveTab("interviews")}>
            <i className="fa-solid fa-calendar-check"></i> Interviews
            <span className="tab-count">{job ? interviews.length : "-"}</span>
          </button>
          <button className={`tab-btn ${activeTab === "details" ? "active" : ""}`} onClick={() => setActiveTab("details")}>
            <i className="fa-solid fa-file-lines"></i> Job Details
          </button>
        </div>

        {/* ================= PIPELINE ================= */}
        {activeTab === "pipeline" && (
          <div id="tab-pipeline" className="pipeline-tab">
            <div className="pipeline-header">
              <div className="stage-filters">
                {STAGE_FILTERS.map((f) => (
                  <button
                    key={f}
                    className={`sf ${stageFilter === f ? "active" : ""}`}
                    onClick={() => setStageFilter(f)}
                  >
                    {f} ({job ? countFor(f) : "-"})
                  </button>
                ))}
              </div>
              <div className="pipeline-search-actions gap-2">
                <input
                  className="pipeline-search"
                  placeholder="Search candidate…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button className="btn btn-primary " onClick={() => openModal("addCand")}>
                  <i className="fa-solid fa-user-plus"></i> Add Candidate
                </button>
              </div>
            </div>

            <div className="cand-list" id="candList">
              {loading && <div className="cand-meta">Loading…</div>}
              {!loading && visibleCandidates.length === 0 && <div className="cand-meta">-</div>}

              {visibleCandidates.map((c) => {
                const cand = getCandidature(c);
                const stage = cand.CandidatureStage || "";
                const rounds = getRounds(c);
                const isRejected = stage === "Rejected";
                const isOffer = stage === "Offer Given";
                const isShortlisted = stage === "Shortlisted";

                return (
                  <div
                    key={c.Guid}
                    className={`cand-card ${isRejected ? "cand-rejected" : ""}`}
                    data-stage={stage}
                  >
                    <div className="cand-av"> {initials(c.FullName)} </div>
                    <div className="cand-info-bx">
                      <div className="cand-name" onClick={isRejected ? undefined : goProfile}>
                        {v(c.FullName)}
                      </div>
                      <div className="cand-meta">
                        <span><i className="fa-solid fa-briefcase"></i> {v(c.ExperienceRange)}</span>
                        <span><i className="fa-solid fa-location-dot"></i> {v(c.Location)}</span>
                        <span><i className="fa-regular fa-calendar"></i> Applied {fmtShort(cand.AppliedOn)}</span>
                        <span><i className="fa-solid fa-phone"></i> {v(c.Phone)}</span>
                      </div>
                    </div>

                    <div className="cand-side-info">
                      <span className={`cand-stage-badge ${stageClass(stage)}`}>{v(stage)}</span>
                      {isRejected ? (
                        <div className="cand-offer-status">{v(cand.RejectionReason)}</div>
                      ) : isOffer ? (
                        <div className="cand-offer-status">Awaiting acceptance</div>
                      ) : (
                        <div className="cand-rounds">
                          {rounds.length === 0 ? (
                            <span className="cand-rounds-empty">No rounds yet</span>
                          ) : (
                            <>
                              <span className="cand-rounds-label"> Rounds: </span>
                              {rounds.map((r) => (
                                <span
                                  key={r.Id}
                                  className={`round-dot ${roundDot(r.RoundStatus)}`}
                                  title={`R${r.RoundNumber}: ${v(r.RoundName)} — ${v(r.RoundStatus)}`}
                                >
                                  {r.RoundNumber}
                                </span>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="cand-actions">
                      <button className="btn btn-outline btn-xs" onClick={() => history.push(`/Recruitment/Candidate-Profile/${c.Guid}`)}>
                        <i className="fa-solid fa-eye"></i> Profile
                      </button>

                      {isOffer && (
                        <button className="btn btn-success btn-xs" onClick={() => openModal("hire", c.FullName)}>
                          <i className="fa-solid fa-check"></i> Mark Joined
                        </button>
                      )}

                      {!isRejected && !isOffer && (
                        <button className="btn btn-primary btn-xs" onClick={() => openModal("move", c.FullName)}>
                          <i className="fa-solid fa-arrow-right"></i> Move
                        </button>
                      )}

                      {!isRejected && !isOffer && !isShortlisted && (
                        <button className="btn-ghost btn-xs" title="Schedule interview" onClick={() => openModal("schedule", c.FullName)}>
                          <i className="fa-solid fa-calendar-plus"></i>
                        </button>
                      )}

                      {isShortlisted && (
                        <button className="btn-ghost btn-xs btn-success-ghost" title="Give offer" onClick={() => openModal("offer", c.FullName)}>
                          <i className="fa-solid fa-file-contract"></i>
                        </button>
                      )}

                      {!isRejected && (
                        <button className="btn-ghost btn-xs btn-danger-ghost" title="Reject" onClick={() => openModal("reject", c.FullName)}>
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ================= INTERVIEWS ================= */}
        {activeTab === "interviews" && (
          <div id="tab-interviews">
            <div className="interviews-actions">
              <button className="btn btn-primary btn-sm" onClick={() => openModal("schedule")}>
                <i className="fa-solid fa-calendar-plus"></i> Schedule Interview
              </button>
            </div>

            {interviews.length === 0 && <div className="ir-meta">-</div>}

            {interviews.map((r) => {
              const d = parseDate(r.ScheduledDate);
              const panel = (r.Panel || []).map((p) => p.Name || p.EmpName || p.FullName).filter(Boolean);
              const isVideo = /video|meet|zoom/i.test(r.InterviewMode || "");
              const isPending = r.RoundStatus === "Pending";

              return (
                <div className="interview-row" key={r.Id}>
                  <div className="ir-date">
                    <div className="ir-day">{d ? d.getDate() : "-"}</div>
                    <div className="ir-month">{d ? d.toLocaleDateString("en-IN", { month: "short" }) : "-"}</div>
                  </div>

                  <div className={`interview-divider ${dividerCls(r.RoundStatus)}`}></div>

                  <div className="ir-info">
                    <div className="ir-name">
                      {v(r.candidateName)} — Round {v(r.RoundNumber)}: {v(r.RoundName)}
                    </div>
                    <div className="ir-meta">
                      <span>
                        <i className="fa-regular fa-clock"></i>
                        {timeRange(r.StartTime, r.DurationMinutes)}
                      </span>
                      <span>
                        <i className={isVideo ? "fa-solid fa-video" : "fa-solid fa-building"}></i>
                        {v(r.InterviewMode)}
                      </span>
                      <span>
                        <i className="fa-solid fa-user"></i>
                        Panel: {panel.length ? panel.join(", ") : "-"}
                      </span>
                    </div>
                  </div>

                  <span className={`ir-status ${roundStatusCls(r.RoundStatus)}`}>{v(r.RoundStatus)}</span>

                  <div className="interview-actions">
                    <button className="btn btn-outline btn-xs" onClick={() => openModal("feedback", r.candidateName)}>
                      <i className="fa-solid fa-star"></i>
                      {isPending ? "Feedback" : "View Feedback"}
                    </button>
                    {isPending && (
                      <button className="btn-ghost btn-xs" title="Reschedule" onClick={() => openModal("reschedule", r.candidateName)}>
                        <i className="fa-solid fa-calendar-pen"></i>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ================= DETAILS ================= */}
        {activeTab === "details" && (
          <div id="tab-details" className="tab-details">
            <div className="details-grid">
              <div>
                <div className="jd-section">
                  <h4>Job Description</h4>
                  <p className="job-description-text">{v(job?.JobDescription)}</p>
                </div>

                <div className="jd-section">
                  <h4>
                    <i className="fa-solid fa-star jd-heading-icon primary"></i>
                    Must Have
                  </h4>
                  <ul className="bullet-ul">
                    {mustReqs.length ? mustReqs.map((r) => <li key={r.Id}>{v(r.RequirementText)}</li>) : <li>-</li>}
                  </ul>
                </div>

                <div className="jd-section">
                  <h4>
                    <i className="fa-solid fa-thumbs-up jd-heading-icon green"></i>
                    Preferred
                  </h4>
                  <ul className="bullet-ul">
                    {prefReqs.length ? prefReqs.map((r) => <li key={r.Id}>{v(r.RequirementText)}</li>) : <li>-</li>}
                  </ul>
                </div>
              </div>

              <div>
                <div className="details-card">
                  <div className="jd-section">
                    <h4>Required Skills</h4>
                    <div className="jd-chips">
                      {mustSkills.length
                        ? mustSkills.map((s) => <span className="skill-chip" key={s.Id}>{v(s.SkillName)}</span>)
                        : <span className="skill-chip">-</span>}
                    </div>
                  </div>

                  <div className="jd-section">
                    <h4>Preferred Skills</h4>
                    <div className="jd-chips">
                      {prefSkills.length
                        ? prefSkills.map((s) => (
                          <span className="skill-chip preferred-skill" key={s.Id}>{v(s.SkillName)}</span>
                        ))
                        : <span className="skill-chip preferred-skill">-</span>}
                    </div>
                  </div>

                  <div className="jd-section quick-info-section">
                    <h4>Quick Info</h4>
                    <div className="quick-info-list">
                      <div className="quick-info-row">
                        <span className="quick-info-label">Openings</span>
                        <strong>{v(job?.NumberOfOpenings)}</strong>
                      </div>
                      <div className="quick-info-row">
                        <span className="quick-info-label">Notice</span>
                        <strong>{job?.NoticePeriodDays != null ? `${job.NoticePeriodDays} days` : "-"}</strong>
                      </div>
                      <div className="quick-info-row">
                        <span className="quick-info-label">Visibility</span>
                        <strong>{v(job?.Visibility)}</strong>
                      </div>
                      <div className="quick-info-row">
                        <span className="quick-info-label">Recruiter</span>
                        <strong>{v(job?.RecruiterName)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="form-footer">
          <button className="btn btn-outline btn-sm" onClick={() => toast("Changes discarded", "warn")}>
            Discard
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => toast("Saved as draft")}>
            <i className="fa-regular fa-floppy-disk"></i>
            Save as Draft
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => toast("Job created and published")}>
            <i className="fa-solid fa-check"></i>
            Create &amp; Publish
          </button>
        </div>
      </div>

      {/* ===================== MOVE MODAL ===================== */}
      <div className={`modal-overlay ${modals.move ? "open" : ""}`} id="moveModal">
        <div className="modal-box">
          <div className="modal-head">
            <div className="modal-icon move-modal-icon"><i className="fa-solid fa-arrow-right"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Move Candidate</div>
              <div className="modal-sub" id="moveCandName">{selectedCandidate}</div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("move")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <p className="modal-description">Select the next stage for this candidate:</p>
            <div className="stage-options">
              <label className="stage-option"><input type="radio" name="stageMove" /><span>Applied</span></label>
              <label className="stage-option"><input type="radio" name="stageMove" /><span>Screening</span></label>
              <label className="stage-option selected"><input type="radio" name="stageMove" defaultChecked /><span>Interview</span></label>
              <label className="stage-option"><input type="radio" name="stageMove" /><span>Shortlisted</span></label>
              <label className="stage-option"><input type="radio" name="stageMove" /><span>Negotiation</span></label>
              <label className="stage-option"><input type="radio" name="stageMove" /><span>Offer Given</span></label>
              <label className="stage-option"><input type="radio" name="stageMove" /><span>Hired / Joined</span></label>
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("move")}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={() => confirmMove()}>
              <i className="fa-solid fa-arrow-right"></i> Move Stage
            </button>
          </div>
        </div>
      </div>

      {/* ===================== SCHEDULE MODAL ===================== */}
      <div className={`modal-overlay ${modals.schedule ? "open" : ""}`} id="scheduleModal">
        <div className="modal-box wide">
          <div className="modal-head">
            <div className="modal-icon schedule-modal-icon"><i className="fa-solid fa-calendar-plus"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Schedule Interview</div>
              <div className="modal-sub" id="scheduleCandName">
                {selectedCandidate || "Select candidate and round details"}
              </div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("schedule")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <div className="fg">
              <label>Candidate <span className="req">*</span></label>
              <select className="og-select" value={selectedCandidate} onChange={(e) => setSelectedCandidate(e.target.value)}>
                {candidateList.length === 0 && <option value="">-</option>}
                {candidateList.map((c) => (
                  <option key={c.Guid} value={c.FullName}>{v(c.FullName)}</option>
                ))}
              </select>
            </div>
            <div className="fg">
              <label>Round Name <span className="req">*</span></label>
              <input className="og-input" placeholder="e.g. Round 1 – Technical Screening" />
            </div>
            <div className="two-column-grid">
              <div className="fg">
                <label>Date <span className="req">*</span></label>
                <input type="date" className="og-input" />
              </div>
              <div className="fg">
                <label>Time <span className="req">*</span></label>
                <input type="time" className="og-input" />
              </div>
            </div>
            <div className="two-column-grid">
              <div className="fg">
                <label>Duration</label>
                <select className="og-select">
                  <option>30 minutes</option>
                  <option>45 minutes</option>
                  <option>60 minutes</option>
                  <option>90 minutes</option>
                </select>
              </div>
              <div className="fg">
                <label>Mode</label>
                <select className="og-select">
                  <option>Video Call</option>
                  <option>In-person</option>
                  <option>Phone</option>
                </select>
              </div>
            </div>
            <div className="fg">
              <label>Panel Members <span className="req">*</span></label>
              <select className="og-select panel-members-select" multiple defaultValue={["Priya Sharma"]}>
                <option value="Priya Sharma">Priya Sharma</option>
                <option value="Arun Mishra">Arun Mishra</option>
                <option value="Rahul Dev">Rahul Dev</option>
                <option value="Neha Gupta">Neha Gupta</option>
              </select>
              <div className="field-hint">Hold Ctrl/Cmd to select multiple</div>
            </div>
            <div className="fg">
              <label>Meeting Link / Location <span className="optional-text">(optional)</span></label>
              <input className="og-input" placeholder="https://meet.google.com/… or office address" />
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("schedule")}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={() => confirmSchedule()}>
              <i className="fa-solid fa-calendar-check"></i> Schedule &amp; Send Invite
            </button>
          </div>
        </div>
      </div>

      {/* ===================== FEEDBACK MODAL ===================== */}
      <div className={`modal-overlay ${modals.feedback ? "open" : ""}`} id="feedbackModal">
        <div className="modal-box wide">
          <div className="modal-head">
            <div className="modal-icon feedback-modal-icon"><i className="fa-solid fa-star"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Interview Feedback</div>
              <div className="modal-sub">{selectedCandidate ? `Round — ${selectedCandidate}` : "-"}</div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("feedback")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <div id="feedbackAspects">
              <div className="feedback-aspect">
                <div className="feedback-aspect-header">
                  <label className="feedback-aspect-label">Technical Skills</label>
                  <div id="rating-1" className="rating-container"></div>
                </div>
                <textarea className="og-textarea" rows="2" placeholder="Comments on technical knowledge, coding skills…"></textarea>
              </div>
              <div className="feedback-aspect">
                <div className="feedback-aspect-header">
                  <label className="feedback-aspect-label">Communication</label>
                  <div id="rating-2" className="rating-container"></div>
                </div>
                <textarea className="og-textarea" rows="2" placeholder="How well does the candidate communicate ideas…"></textarea>
              </div>
              <div className="feedback-aspect">
                <div className="feedback-aspect-header">
                  <label className="feedback-aspect-label">Attitude &amp; Culture Fit</label>
                  <div id="rating-3" className="rating-container"></div>
                </div>
                <textarea className="og-textarea" rows="2" placeholder="Attitude, team fit, growth mindset…"></textarea>
              </div>
              <div className="feedback-aspect">
                <div className="feedback-aspect-header">
                  <label className="feedback-aspect-label">Problem Solving</label>
                  <div id="rating-4" className="rating-container"></div>
                </div>
                <textarea className="og-textarea" rows="2" placeholder="Approach to problem-solving, reasoning…"></textarea>
              </div>
            </div>
            <div className="feedback-recommendation">
              <label className="feedback-recommendation-label">Overall Recommendation</label>
              <div className="recommendation-options">
                <label className="recommendation-option">
                  <input type="radio" name="rec" className="recommendation-proceed" />
                  <span className="recommendation-proceed-text">Proceed</span>
                </label>
                <label className="recommendation-option">
                  <input type="radio" name="rec" className="recommendation-maybe" />
                  <span className="recommendation-maybe-text">Maybe</span>
                </label>
                <label className="recommendation-option">
                  <input type="radio" name="rec" className="recommendation-reject" />
                  <span className="recommendation-reject-text">Reject</span>
                </label>
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("feedback")}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={() => submitFeedback()}>
              <i className="fa-solid fa-check"></i> Submit Feedback
            </button>
          </div>
        </div>
      </div>

      {/* ===================== REJECT MODAL ===================== */}
      <div className={`modal-overlay ${modals.reject ? "open" : ""}`} id="rejectModal">
        <div className="modal-box">
          <div className="modal-head">
            <div className="modal-icon reject-modal-icon"><i className="fa-solid fa-xmark"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Reject Candidate</div>
              <div className="modal-sub" id="rejectCandName">{selectedCandidate}</div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("reject")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <div className="fg">
              <label>Reason for rejection <span className="req">*</span></label>
              <select className="og-select">
                <option value="">Select reason…</option>
                <option>Skills mismatch</option>
                <option>Experience below requirement</option>
                <option>Better candidate selected</option>
                <option>Salary expectation mismatch</option>
                <option>Position filled</option>
                <option>Candidate withdrew</option>
                <option>Other</option>
              </select>
            </div>
            <div className="fg">
              <label>Notes <span className="optional-text">(internal only)</span></label>
              <textarea className="og-textarea" rows="3" placeholder="Add internal notes…"></textarea>
            </div>
            <label className="reject-email-option">
              <input type="checkbox" />
              Send rejection email to candidate
            </label>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("reject")}>Cancel</button>
            <button className="btn btn-danger btn-sm" onClick={() => confirmReject()}>
              <i className="fa-solid fa-xmark"></i> Confirm Rejection
            </button>
          </div>
        </div>
      </div>

      {/* ===================== ADD CANDIDATE MODAL ===================== */}
      <div className={`modal-overlay ${modals.addCand ? "open" : ""}`} id="addCandModal">
        <div className="modal-box">
          <div className="modal-head">
            <div className="modal-icon add-candidate-modal-icon"><i className="fa-solid fa-user-plus"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Add Candidate to Job</div>
              <div className="modal-sub">{jobLabel}</div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("addCand")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <div className="candidate-tab-buttons">
              <button
                className={`btn btn-sm candidate-tab-button ${addCandTab === "existing" ? "btn-outline" : "btn-secondary"}`}
                id="existingBtn"
                onClick={() => showAddTab("existing")}
              >
                <i className="fa-solid fa-search"></i> Existing Candidate
              </button>
              <button
                type="button"
                className={`btn btn-sm candidate-tab-button ${addCandTab === "new" ? "btn-outline" : "btn-secondary"
                  }`}
                id="newBtn"
                onClick={() => history.push("/Recruitment/Recruitment-Candidate-Add")}
              >
                <i className="fa-solid fa-plus"></i> New Candidate
              </button>
            </div>

            <div className="fg">
              <label>Search candidate database</label>
              <input className="og-input" placeholder="Name, email or phone…" />
            </div>

            <div className="candidate-list">
              <div
                className="candidate-list-item"
                onClick={(e) => {
                  e.currentTarget.style.background = "var(--primary-lt)";
                  e.currentTarget.querySelector('input[type="radio"]').checked = true;
                }}
              >
                <input type="radio" name="addCand" className="candidate-radio" />
                <div className="cand-av candidate-avatar-small candidate-avatar-meera">MJ</div>
                <div>
                  <div className="candidate-name">Meera Joshi</div>
                  <div className="candidate-meta">4 yrs · Delhi · React, Vue</div>
                </div>
              </div>
              <div
                className="candidate-list-item"
                onClick={(e) => {
                  e.currentTarget.style.background = "var(--primary-lt)";
                  e.currentTarget.querySelector('input[type="radio"]').checked = true;
                }}
              >
                <input type="radio" name="addCand" className="candidate-radio" />
                <div className="cand-av candidate-avatar-small candidate-avatar-siddharth">SR</div>
                <div>
                  <div className="candidate-name">Siddharth Rao</div>
                  <div className="candidate-meta">5 yrs · Bengaluru · React, TypeScript</div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("addCand")}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={() => confirmAddCand()}>
              <i className="fa-solid fa-user-plus"></i> Add to Pipeline
            </button>
          </div>
        </div>
      </div>

      {/* ===================== HIRE MODAL ===================== */}
      <div className={`modal-overlay ${modals.hire ? "open" : ""}`} id="hireModal">
        <div className="modal-box">
          <div className="modal-head">
            <div className="modal-icon hire-modal-icon"><i className="fa-solid fa-trophy"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Mark as Joined</div>
              <div className="modal-sub" id="hireCandName">{selectedCandidate}</div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("hire")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <p className="modal-description-spaced">
              This will mark the candidate as Hired/Joined. Once confirmed, you can create an employee record
              from the candidate profile.
            </p>
            <div className="fg">
              <label>Joining Date <span className="req">*</span></label>
              <input type="date" className="og-input" />
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("hire")}>Cancel</button>
            <button className="btn btn-primary btn-sm hire-confirm-button" onClick={() => confirmHire()}>
              <i className="fa-solid fa-check"></i> Confirm Joining
            </button>
          </div>
        </div>
      </div>

      {/* ===================== OFFER MODAL ===================== */}
      <div className={`modal-overlay ${modals.offer ? "open" : ""}`} id="offerModal">
        <div className="modal-box">
          <div className="modal-head">
            <div className="modal-icon offer-modal-icon"><i className="fa-solid fa-file-contract"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Give Offer</div>
              <div className="modal-sub" id="offerCandName">{selectedCandidate}</div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("offer")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <div className="fg">
              <label>Offer Date <span className="req">*</span></label>
              <input type="date" className="og-input" />
            </div>
            <div className="fg">
              <label>Offer Letter PDF <span className="optional-text">(optional)</span></label>
              <input type="file" accept=".pdf" className="og-input offer-file-input" />
            </div>
            <div className="fg">
              <label>Notes</label>
              <textarea className="og-textarea" rows="2" placeholder="Internal notes about this offer…"></textarea>
            </div>
            <div className="offer-info-box">
              <i className="fa-solid fa-circle-info"></i>
              Candidate will receive an email to accept or reject this offer on the ATS portal.
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("offer")}>Cancel</button>
            <button className="btn btn-primary btn-sm offer-submit-button" onClick={() => confirmOffer()}>
              <i className="fa-solid fa-paper-plane"></i> Send Offer
            </button>
          </div>
        </div>
      </div>

      {/* ===================== HOLD MODAL ===================== */}
      <div className={`modal-overlay ${modals.hold ? "open" : ""}`} id="holdModal">
        <div className="modal-box">
          <div className="modal-head">
            <div className="modal-icon hold-modal-icon"><i className="fa-solid fa-pause"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Put Job on Hold?</div>
              <div className="modal-sub">{jobLabel}</div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("hold")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <p className="modal-description">
              The job will be paused. Candidates in the pipeline will not be affected.
            </p>
            <textarea className="og-textarea" rows="2" placeholder="Reason for hold (optional)…"></textarea>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("hold")}>Cancel</button>
            <button
              className="btn btn-warn btn-sm"
              onClick={() => { closeModal("hold"); toast("Job put on hold", "warn"); }}
            >
              <i className="fa-solid fa-pause"></i> Put on Hold
            </button>
          </div>
        </div>
      </div>

      {/* ===================== CLOSE MODAL ===================== */}
      <div className={`modal-overlay ${modals.close ? "open" : ""}`} id="closeModal">
        <div className="modal-box">
          <div className="modal-head">
            <div className="modal-icon close-job-modal-icon"><i className="fa-solid fa-xmark"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Close This Job?</div>
              <div className="modal-sub">{jobLabel}</div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("close")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <p className="modal-description">
              Closing stops new applications. Active candidates remain in the pipeline.
            </p>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("close")}>Cancel</button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => { closeModal("close"); toast("Job closed successfully", "warn"); }}
            >
              <i className="fa-solid fa-xmark"></i> Close Job
            </button>
          </div>
        </div>
      </div>

      {/* ===================== RESCHEDULE MODAL ===================== */}
      <div className={`modal-overlay ${modals.reschedule ? "open" : ""}`} id="rescheduleModal">
        <div className="modal-box">
          <div className="modal-head">
            <div className="modal-icon reschedule-modal-icon"><i className="fa-solid fa-calendar-pen"></i></div>
            <div className="modal-title-wrap">
              <div className="modal-title">Reschedule Interview</div>
            </div>
            <button className="modal-close-btn" onClick={() => closeModal("reschedule")}>
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <div className="two-column-grid">
              <div className="fg">
                <label>New Date <span className="req">*</span></label>
                <input type="date" className="og-input" />
              </div>
              <div className="fg">
                <label>New Time <span className="req">*</span></label>
                <input type="time" className="og-input" />
              </div>
            </div>
            <div className="fg">
              <label>Reason</label>
              <textarea className="og-textarea" rows="2" placeholder="Why is the interview being rescheduled?"></textarea>
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => closeModal("reschedule")}>Cancel</button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => { closeModal("reschedule"); toast("Interview rescheduled and invite sent"); }}
            >
              <i className="fa-solid fa-check"></i> Reschedule &amp; Notify
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default JobDetailRecruitment;